import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n } from '@/i18n'
import { enqueueCommand } from '@/offline'
import { Banner, BilingualLabel, Button, Card, CardBody, CardHeader, useToast } from '@/ui'
import type { MarkBagsConvPayload } from '../commands'
import '../commands'
import { placePath, type BagView, type StoreState } from '../projection'
import { classifyScan, redTieList } from '../rules'
import { BagLine, Counter, ScanButton } from '../components/bits'
import { useRefusalText } from '../components/useRefusalText'
import { StoreScreen } from '../components/StoreScreen'
import { useDraftState, useNow } from '../useStore'

interface RedTieDraft {
  items: Array<{ container_id: string; scanned_at: string }>
}

/**
 * F5.6-j / F5-B1.c-c — Red ties: tie a red tie on every bag of a lot moved to ordinary (and on an
 * orphan sack), scan each one (mark_bags_conv), then move them to the red-tie bay with Move.
 */
export function RedTiePage() {
  return (
    <StoreScreen title="store.redTie">
      {(state) => <RedTieFlow state={state} />}
    </StoreScreen>
  )
}

function RedTieFlow({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const now = useNow()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<RedTieDraft>(
    `store.redtie:${state.siteId}`,
    'store.redtie',
    () => ({ items: [] }),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [sent, setSent] = useState(false)

  const pending = useMemo(() => redTieList(state), [state])
  // Orphan sacks (evidence NONE) made on this phone may be tied before the next download.
  const orphanIds = new Set(
    state.replacements.filter((r) => r.evidence === 'NONE').map((r) => r.payload.new_container.id),
  )
  const allowed = new Set([...pending.map((b) => b.container_id), ...orphanIds])
  const scanned = draft.items
    .map((i) => state.bags.get(i.container_id))
    .filter((b): b is BagView => !!b)
  const convBay = [...state.locations.values()].find((l) => l.role === 'CONVENTIONAL')

  const byLot = new Map<string, BagView[]>()
  for (const b of pending) byLot.set(b.unit_id, [...(byLot.get(b.unit_id) ?? []), b])

  const confirm = async () => {
    if (!scanned.length) return
    setSaving(true)
    try {
      const payload: MarkBagsConvPayload = {
        items: draft.items.map((i) => ({
          container_id: i.container_id,
          qr_payload: state.bags.get(i.container_id)!.qr_payload,
          scanned_at: i.scanned_at,
        })),
      }
      const parents = [
        ...new Set(scanned.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      await enqueueCommand({
        type: 'mark_bags_conv',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      await clear()
      setSent(true)
      toast.success(t('store.redTieDone', { count: payload.items.length }))
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  return (
    <div className="flex flex-col gap-4">
      {sent && convBay && (
        <Banner
          tone="success"
          action={
            <Link
              to={`/m/store/move?to=${encodeURIComponent(convBay.id)}`}
              className="font-mono text-[12px] font-bold uppercase underline"
            >
              {t('store.move')}
            </Link>
          }
        >
          {t('store.redTieMoveNext', { bay: convBay.code })}
        </Banner>
      )}
      {pending.length === 0 && orphanIds.size === 0 && !sent && (
        <Banner tone="info">{t('store.redTieNone')}</Banner>
      )}
      {byLot.size > 0 && (
        <Card>
          <CardHeader eyebrow={t('store.redTieList', { count: pending.length })} />
          <CardBody className="py-1">
            <ul>
              {[...byLot.entries()].map(([unit, list]) => (
                <li key={unit} className="border-b border-rule/70 py-2 last:border-b-0">
                  <div className="flex justify-between">
                    <span className="font-mono">{state.lots.get(unit)?.code}</span>
                    <span className="font-mono">{list.length}</span>
                  </div>
                  <div className="text-[12px] text-muted">
                    {[...new Set(list.map((b) => placePath(state, b.location_id)))].join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Counter label={t('store.bags')} value={scanned.length} />
      <ScanButton
        variant="accent"
        label={<BilingualLabel k="store.redTieScan" />}
        validate={(c) => {
          const k = classifyScan(state, c)
          if (k.kind !== 'BAG') return refusal(k.kind === 'DEAD' ? 'TAG_VOID' : 'TAG_WRONG_KIND')
          if (draft.items.some((i) => i.container_id === k.bag.container_id))
            return refusal('DUPLICATE')
          return allowed.has(k.bag.container_id) ? null : t('store.redTieNotListed')
        }}
        onScan={(c) => {
          const k = classifyScan(state, c)
          if (k.kind !== 'BAG') return
          update((d) =>
            d.items.some((i) => i.container_id === k.bag.container_id)
              ? d
              : {
                  items: [
                    ...d.items,
                    { container_id: k.bag.container_id, scanned_at: new Date().toISOString() },
                  ],
                },
          )
        }}
      />
      {scanned.length > 0 && (
        <Card>
          <CardBody className="py-1">
            <ul>
              {scanned.map((b) => (
                <BagLine
                  key={b.container_id}
                  state={state}
                  bag={b}
                  now={now}
                  onRemove={() =>
                    update((d) => ({
                      items: d.items.filter((i) => i.container_id !== b.container_id),
                    }))
                  }
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Button
        size="xl"
        fullWidth
        disabled={!scanned.length}
        loading={saving}
        onClick={() => void confirm()}
      >
        <BilingualLabel k="store.place.confirm" />
      </Button>
    </div>
  )
}
